import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { User, Mail, Calendar, Save } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await userAPI.updateProfile({ name, email });
      updateUser(response.data);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-8 animate-fade-in max-w-2xl" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Profile
        </h1>
        <p className="text-stone-500 mt-1">
          Manage your account settings
        </p>
      </div>

      {/* Profile Card */}
      <Card className="bg-white border border-stone-200 rounded-lg shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-stone-200 flex items-center justify-center text-stone-600 text-2xl font-bold">
              {user.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <CardTitle className="text-xl font-semibold text-[#021d49]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {user.name}
              </CardTitle>
              <p className="text-stone-500">{user.email}</p>
            </div>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4 text-stone-400" />
                Full Name
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-[4px]"
                data-testid="profile-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-stone-400" />
                Email Address
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-[4px]"
                data-testid="profile-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-stone-400" />
                Member Since
              </Label>
              <Input
                value={user.created_at ? format(parseISO(user.created_at), 'MMMM d, yyyy') : 'N/A'}
                disabled
                className="rounded-[4px] bg-stone-50"
              />
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px]"
                data-testid="profile-save-btn"
              >
                {saving ? (
                  'Saving...'
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
